/**
 * Общие AI-агенты и их привязки к проектам (docs/17, docs/18).
 *
 * Control-токен отдаёт только список привязок; доменные операции выполняются
 * проектным API-токеном из ответа. Секреты в логах и аудите не появляются.
 */

import { z } from 'zod';

import { AgentStatusNamesSchema } from './agent-setup.js';
import { AgentRoleSchema, IsoDateTimeSchema, UuidSchema } from './common.js';
import { DeveloperChecksSchema, GitProviderSchema } from './project.js';

/** Роли, для которых существует общий singleton-агент. */
export const SharedAgentRoleSchema = z.enum(['analyst', 'developer']);
export type SharedAgentRole = z.infer<typeof SharedAgentRoleSchema>;

export const CreateSharedAgentBodySchema = z
  .object({
    agentRole: SharedAgentRoleSchema.default('developer'),
    displayName: z.string().trim().min(1).max(80).optional(),
  })
  .strict();
export type CreateSharedAgentBody = z.infer<typeof CreateSharedAgentBodySchema>;

/** Привязка общего агента к проекту (без секретов). */
export const AgentBindingSchema = z.object({
  id: UuidSchema,
  agentId: UuidSchema,
  agentRole: AgentRoleSchema,
  project: z.object({ id: UuidSchema, key: z.string(), name: z.string() }),
  statuses: AgentStatusNamesSchema,
  isActive: z.boolean(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type AgentBinding = z.infer<typeof AgentBindingSchema>;

export const AgentBindingListResponseSchema = z.object({
  items: z.array(AgentBindingSchema),
});
export type AgentBindingListResponse = z.infer<typeof AgentBindingListResponseSchema>;

export const CreateAgentBindingBodySchema = z.object({ agentId: UuidSchema }).strict();
export type CreateAgentBindingBody = z.infer<typeof CreateAgentBindingBodySchema>;

export const UpdateAgentBindingBodySchema = z
  .object({ statuses: AgentStatusNamesSchema })
  .strict();
export type UpdateAgentBindingBody = z.infer<typeof UpdateAgentBindingBodySchema>;

/** Репозиторий, который AI-разработчик получает по control-токену. */
export const AgentBindingRepositorySchema = z.object({
  url: z.string(),
  branch: z.string(),
  provider: GitProviderSchema,
  providerBaseUrl: z.string().nullable(),
  writeToken: z.string(),
  checks: DeveloperChecksSchema,
});
export type AgentBindingRepository = z.infer<typeof AgentBindingRepositorySchema>;

export const AgentBindingPublicSchema = z.object({
  projectId: UuidSchema,
  projectKey: z.string(),
  projectName: z.string(),
  token: z.string(),
  repo: AgentBindingRepositorySchema,
  statuses: AgentStatusNamesSchema,
});
export type AgentBindingPublic = z.infer<typeof AgentBindingPublicSchema>;

/** Репозиторий AI-аналитика: только чтение, Git-токен может быть пустым. */
export const AnalystBindingRepositorySchema = z.object({
  url: z.string(),
  branch: z.string(),
  token: z.string().nullable(),
});
export type AnalystBindingRepository = z.infer<typeof AnalystBindingRepositorySchema>;

export const AnalystBindingPublicSchema = z.object({
  projectId: UuidSchema,
  projectKey: z.string(),
  projectName: z.string(),
  token: z.string(),
  repo: AnalystBindingRepositorySchema,
  statuses: AgentStatusNamesSchema,
});
export type AnalystBindingPublic = z.infer<typeof AnalystBindingPublicSchema>;

export const AgentBindingsResponseSchema = z.discriminatedUnion('role', [
  z.object({
    agentId: UuidSchema,
    role: z.literal('developer'),
    version: z.number().int().min(0),
    generatedAt: IsoDateTimeSchema,
    items: z.array(AgentBindingPublicSchema),
  }),
  z.object({
    agentId: UuidSchema,
    role: z.literal('analyst'),
    version: z.number().int().min(0),
    generatedAt: IsoDateTimeSchema,
    items: z.array(AnalystBindingPublicSchema),
  }),
]);
export type AgentBindingsResponse = z.infer<typeof AgentBindingsResponseSchema>;
